import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Volume2, VolumeX,
    Settings2, Loader, Heart, Share2, Eye, Link as LinkIcon
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import RotaryKnob from '../components/RotaryKnob';
import PlayerVisualizer, { VISUALIZER_STYLES, COLOR_SCHEMES } from '../components/PlayerVisualizer';
import PlayerTrackList from '../components/PlayerTrackList';
import AccessRequiredModal from '../components/AccessRequiredModal';
import ShareModal from '../components/ShareModal';
import { isGated } from '../utils/accessLabels';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import trackingService from '../services/tracking';
import { updatePageMeta } from '../utils/metaUtils';

// Real pause between autoplay-advanced tracks. Mobile browsers (notably iOS Safari)
// revoke autoplay permission once a setTimeout breaks the play() call out of the
// native 'ended' event's synchronous chain, so we still call play() synchronously here
// to keep the element "warm" — but pause it again immediately (same tick, before any
// audible samples are output), hold it paused at the start of the track for the gap,
// then resume for real when the timer fires. No part of the track is skipped; the gap
// is silence from an actually-paused element, not muted-but-playing audio.
const AUTOPLAY_GAP_MS = 1000;

const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const dedupeTracks = (list) => {
    const unique = [];
    const seen = new Set();
    for (const track of list) {
        const key = track.stored_filename || track.original_filename || track.title || track.file_url;
        if (key && !seen.has(key)) {
            seen.add(key);
            unique.push(track);
        }
    }
    return unique;
};

const Player = () => {
    const navigate = useNavigate();
    const { isAuthenticated, hasActiveSubscription } = useAuth();

    // 'deepwave' = only The Deep Wave's own uploads; 'community' = every public
    // audio upload on the platform (including The Deep Wave).
    const [catalogMode, setCatalogMode] = useState('deepwave');
    const [deepWaveTracks, setDeepWaveTracks] = useState([]);
    const [deepWaveLoading, setDeepWaveLoading] = useState(true);
    const [deepWaveError, setDeepWaveError] = useState(null);
    const [communityTracks, setCommunityTracks] = useState([]);
    const [communityLoading, setCommunityLoading] = useState(false);
    const [communityError, setCommunityError] = useState(null);
    const [communityFetched, setCommunityFetched] = useState(false);

    const tracks = useMemo(
        () => (catalogMode === 'community' ? communityTracks : deepWaveTracks),
        [catalogMode, communityTracks, deepWaveTracks]
    );
    const loading = catalogMode === 'community' ? communityLoading : deepWaveLoading;
    const error = catalogMode === 'community' ? communityError : deepWaveError;

    const [currentIndex, setCurrentIndex] = useState(-1);
    // Mirrors currentTrackRef into state purely for display (title/cover art) — kept
    // independent of `tracks`/currentIndex so switching catalogMode never blanks out
    // what's actually playing, even if the current track isn't in the new list.
    const [currentTrackState, setCurrentTrackState] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isShuffled, setIsShuffled] = useState(false);
    const [isAutoplay, setIsAutoplay] = useState(true);

    const [volume, setVolume] = useState(0.8);
    const [isMuted, setIsMuted] = useState(false);
    const [bassBoost, setBassBoost] = useState(0);

    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    // track.duration from the API is often null (never captured at upload time for older
    // tracks) — cache the real duration per track id as each one actually loads, so the
    // catalog list keeps showing a correct length even after playback moves to another track.
    const [trackDurations, setTrackDurations] = useState({});

    const [visualizerStyle, setVisualizerStyle] = useState('bars');
    const [colorScheme, setColorScheme] = useState('wave');
    const [showSettings, setShowSettings] = useState(false);
    const [visualizerSettings, setVisualizerSettings] = useState({
        sensitivity: 1.2,
        glowIntensity: 0.5,
        particleCount: 50,
        lineThickness: 2,
        mirrorMode: false,
    });

    const [accessModal, setAccessModal] = useState({ isOpen: false, track: null });
    const [shareModal, setShareModal] = useState({ isOpen: false, track: null });
    const [linkCopied, setLinkCopied] = useState(false);
    const [analyserReady, setAnalyserReady] = useState(false);

    const audioRef = useRef(null);
    const seekBarRef = useRef(null);
    const audioCtxRef = useRef(null);
    const sourceRef = useRef(null);
    const bassFilterRef = useRef(null);
    const analyserRef = useRef(null);
    const isWiredRef = useRef(false);
    const isPlayingRef = useRef(false);
    const prevVolumeRef = useRef(0.8);
    const shuffleHistoryRef = useRef([]);
    // Pending "resume playback after the between-tracks pause" timer — cancelled/
    // superseded whenever a new track starts before it fires.
    const gapResumeTimeoutRef = useRef(null);
    // Tracks the currently-loaded track synchronously (setCurrentIndex is
    // async/batched, so audio event handlers that fire quickly after a track
    // switch could otherwise read a stale index before React re-renders).
    const currentTrackRef = useRef(null);

    // ── Page metadata (SEO / social previews) ──────────────────────
    useEffect(() => {
        updatePageMeta({
            title: 'The Deep Wave Audio Player',
            description: 'Stream The Deep Wave\'s full music catalog on an all-access radio deck — volume and bass controls, shuffle, autoplay, and a live audio visualizer.',
            keywords: 'the deep wave, music player, audio player, radio, streaming, visualizer, music catalog',
            ogType: 'music.playlist',
            canonical: 'https://thedeepwave.com/radio'
        });
    }, []);

    // ── Fetch The Deep Wave's own catalog ──────────────────────────
    useEffect(() => {
        const fetchTracks = async () => {
            try {
                setDeepWaveLoading(true);
                setDeepWaveError(null);
                const response = await api.get('/upload/public/audio?username=TheDeepWave&limit=100');
                if (response.data.success) {
                    const filtered = response.data.data.filter(t => t.user_id === 7);
                    setDeepWaveTracks(dedupeTracks(filtered));
                } else {
                    setDeepWaveError('Unable to load The Deep Wave catalog.');
                }
            } catch (err) {
                console.error('Error fetching player catalog:', err);
                setDeepWaveError('Unable to load The Deep Wave catalog. Please try again later.');
            } finally {
                setDeepWaveLoading(false);
            }
        };
        fetchTracks();
    }, []);

    // Community roll — every public audio upload on the platform, fetched lazily the
    // first time the user actually switches to it (no point loading it up front).
    useEffect(() => {
        if (catalogMode !== 'community' || communityFetched) return;
        const fetchCommunityTracks = async () => {
            try {
                setCommunityLoading(true);
                setCommunityError(null);
                const response = await api.get('/upload/public/audio?limit=100');
                if (response.data.success) {
                    setCommunityTracks(dedupeTracks(response.data.data));
                } else {
                    setCommunityError('Unable to load the community audio roll.');
                }
            } catch (err) {
                console.error('Error fetching community audio roll:', err);
                setCommunityError('Unable to load the community audio roll. Please try again later.');
            } finally {
                setCommunityLoading(false);
                setCommunityFetched(true);
            }
        };
        fetchCommunityTracks();
    }, [catalogMode, communityFetched]);

    // Keep the highlighted row in sync with whatever's actually loaded when the user
    // switches catalogMode — the same track may sit at a different index (or not exist)
    // in the other list.
    useEffect(() => {
        if (!currentTrackRef.current) return;
        const idx = tracks.findIndex(t => t.id === currentTrackRef.current.id);
        setCurrentIndex(idx);
    }, [tracks]);

    // track.duration is often null in the DB (never captured at upload time for older
    // tracks) — probe each track's real file directly so the Audio Roll shows correct
    // lengths immediately, without waiting for the user to actually play each one.
    useEffect(() => {
        if (tracks.length === 0) return;
        const probes = [];
        tracks.forEach((track) => {
            if (track.duration || !track.file_url) return;
            const probe = new Audio();
            probe.preload = 'metadata';

            const cleanup = () => {
                probe.removeEventListener('loadedmetadata', onLoadedMetadata);
                probe.removeEventListener('timeupdate', onTimeUpdate);
                probe.removeEventListener('error', cleanup);
                probe.src = '';
            };
            const finish = (value) => {
                if (isFinite(value) && value > 0) {
                    setTrackDurations((prev) => ({ ...prev, [track.id]: value }));
                }
                cleanup();
            };
            // Some MP3s (missing a proper VBR/Xing header) report duration as
            // Infinity until the browser is forced to seek near the end — a
            // long-standing Chrome/Firefox quirk, not a data problem.
            const onTimeUpdate = () => finish(probe.duration);
            const onLoadedMetadata = () => {
                if (isFinite(probe.duration)) {
                    finish(probe.duration);
                } else {
                    probe.addEventListener('timeupdate', onTimeUpdate);
                    probe.currentTime = 1e101;
                }
            };

            probe.addEventListener('loadedmetadata', onLoadedMetadata);
            probe.addEventListener('error', cleanup);
            probe.src = track.file_url;
            probes.push(probe);
        });
        return () => probes.forEach((probe) => { probe.src = ''; });
    }, [tracks]);

    const isTrackGated = useCallback((track) => (
        track ? isGated(track.access_level, isAuthenticated, hasActiveSubscription) : false
    ), [isAuthenticated, hasActiveSubscription]);

    const trackViewUrl = (track) => track
        ? `/view/audio/${track.username || 'TheDeepWave'}/${track.stored_filename || track.original_filename || track.id}`
        : null;

    // ── Web Audio graph — wired once, lazily, on first play ────────
    const wireAudioGraph = useCallback(() => {
        if (isWiredRef.current || !audioRef.current) return;
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const source = ctx.createMediaElementSource(audioRef.current);
        const bassFilter = ctx.createBiquadFilter();
        bassFilter.type = 'lowshelf';
        bassFilter.frequency.value = 200;
        bassFilter.gain.value = bassBoost;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;

        source.connect(bassFilter);
        bassFilter.connect(analyser);
        analyser.connect(ctx.destination);

        audioCtxRef.current = ctx;
        sourceRef.current = source;
        bassFilterRef.current = bassFilter;
        analyserRef.current = analyser;
        isWiredRef.current = true;
        setAnalyserReady(true);
    }, [bassBoost]);

    // ── Track selection / playback ──────────────────────────────────
    const playTrackAt = useCallback((index, { userInitiated, autoPlay = true, gapMs = 0 } = {}) => {
        if (gapResumeTimeoutRef.current) {
            clearTimeout(gapResumeTimeoutRef.current);
            gapResumeTimeoutRef.current = null;
        }

        const track = tracks[index];
        if (!track) return;

        if (isTrackGated(track)) {
            if (userInitiated) {
                setAccessModal({ isOpen: true, track });
            } else {
                advanceSkippingLockedRef.current(index, 1, gapMs);
            }
            return;
        }

        const audio = audioRef.current;
        if (!audio) return;

        if (index !== currentIndex) {
            if (currentTrackRef.current) {
                trackingService.stopPlayTracking(currentTrackRef.current.id);
            }
            currentTrackRef.current = track;
            setCurrentIndex(index);
            setCurrentTrackState(track);
            audio.src = track.file_url;
            audio.load();
        }

        // Loading the initial track on page mount shouldn't attempt playback —
        // browsers block unprompted autoplay anyway, and this just populates the
        // deck (title/cover art/seek bar) so it's ready the moment the user hits play.
        if (!autoPlay) return;

        wireAudioGraph();
        if (bassFilterRef.current) bassFilterRef.current.gain.value = bassBoost;
        if (audioCtxRef.current?.state === 'suspended') {
            audioCtxRef.current.resume();
        }

        // audio.volume persists across src changes on this reused <audio> element,
        // so always set it explicitly.
        audio.volume = isMuted ? 0 : volume;

        if (gapMs > 0) {
            // Play then immediately pause again, synchronously, in the same tick —
            // this is what keeps the element eligible for autoplay on iOS (see
            // AUTOPLAY_GAP_MS comment) without letting any real audio reach the
            // speakers. The pause() below interrupts the play() request, which
            // throws an AbortError in some browsers; that's expected here, not
            // a real failure.
            const playPromise = audio.play();
            audio.pause();
            audio.currentTime = 0;
            playPromise?.catch(() => {});

            gapResumeTimeoutRef.current = setTimeout(() => {
                gapResumeTimeoutRef.current = null;
                const resumeAudio = audioRef.current;
                if (!resumeAudio) return;
                // Re-assert position 0 here, not just right after the priming pause
                // above. At that point the track has no data buffered yet, so some
                // browsers (seen on Android Chrome) silently ignore the seek — the
                // gap gives it time to buffer, but by the time it does, position can
                // have drifted forward from whatever the priming play() reached
                // before pause() took effect. Reset it again now that data exists.
                resumeAudio.currentTime = 0;
                resumeAudio.play().catch(err => console.error('Player play error:', err));
            }, gapMs);
        } else {
            audio.play().catch(err => console.error('Player play error:', err));
        }
    }, [tracks, currentIndex, isTrackGated, wireAudioGraph, bassBoost, isMuted, volume]);

    const advanceSkippingLocked = useCallback((startIndex, direction, gapMs = 0) => {
        if (tracks.length === 0) return;
        for (let step = 1; step <= tracks.length; step++) {
            const idx = ((startIndex + direction * step) % tracks.length + tracks.length) % tracks.length;
            if (!isTrackGated(tracks[idx])) {
                playTrackAt(idx, { userInitiated: false, gapMs });
                return;
            }
        }
        // Entire catalog is gated for this user — nothing accessible to play.
        setIsPlaying(false);
    }, [tracks, isTrackGated, playTrackAt]);

    // playTrackAt references advanceSkippingLocked before it's defined (mutual
    // recursion) — bridge via a ref so both can stay as plain useCallbacks.
    const advanceSkippingLockedRef = useRef(() => {});
    useEffect(() => { advanceSkippingLockedRef.current = advanceSkippingLocked; }, [advanceSkippingLocked]);

    // Load the first accessible track into the deck as soon as the catalog is ready,
    // instead of leaving it empty with "Select a track to begin" until a manual click.
    // Runs once — doesn't fire again on later catalogMode switches or refetches.
    const hasAutoSelectedRef = useRef(false);
    useEffect(() => {
        if (hasAutoSelectedRef.current || currentIndex >= 0 || tracks.length === 0) return;
        const firstAccessible = tracks.findIndex(t => !isTrackGated(t));
        if (firstAccessible >= 0) {
            hasAutoSelectedRef.current = true;
            playTrackAt(firstAccessible, { userInitiated: false, autoPlay: false });
        }
    }, [tracks, currentIndex, isTrackGated, playTrackAt]);

    const handlePlayPause = () => {
        const audio = audioRef.current;
        if (currentIndex < 0) {
            // Nothing loaded yet — start at the first accessible track.
            advanceSkippingLocked(-1, 1);
            return;
        }
        if (!audio) return;
        if (isPlaying) {
            audio.pause();
        } else {
            wireAudioGraph();
            if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
            audio.play().catch(err => console.error('Player play error:', err));
        }
    };

    const handleNext = () => advanceSkippingLocked(currentIndex, 1);
    const handlePrev = () => {
        const last = shuffleHistoryRef.current.pop();
        if (last !== undefined && !isTrackGated(tracks[last])) {
            playTrackAt(last, { userInitiated: false });
        } else {
            advanceSkippingLocked(currentIndex, -1);
        }
    };

    const handleShuffleNow = () => {
        const accessible = tracks
            .map((t, i) => i)
            .filter(i => i !== currentIndex && !isTrackGated(tracks[i]));
        if (accessible.length === 0) return;
        const next = accessible[Math.floor(Math.random() * accessible.length)];
        if (currentIndex >= 0) shuffleHistoryRef.current.push(currentIndex);
        playTrackAt(next, { userInitiated: false });
    };

    const handleSelectTrack = (track, index) => {
        if (isTrackGated(track)) {
            setAccessModal({ isOpen: true, track });
        } else {
            if (currentIndex >= 0) shuffleHistoryRef.current.push(currentIndex);
            playTrackAt(index, { userInitiated: true });
        }
    };

    const handleEnded = () => {
        // The track that just finished is done being tracked regardless of what
        // happens next.
        if (currentTrackRef.current) trackingService.stopPlayTracking(currentTrackRef.current.id);

        if (!isAutoplay) {
            setIsPlaying(false);
            return;
        }

        // Must advance (and call audio.play()) synchronously within this native
        // 'ended' handler — see AUTOPLAY_GAP_MS comment above.
        if (isShuffled) {
            const accessible = tracks.map((t, i) => i).filter(i => i !== currentIndex && !isTrackGated(tracks[i]));
            if (accessible.length === 0) { setIsPlaying(false); return; }
            const next = accessible[Math.floor(Math.random() * accessible.length)];
            shuffleHistoryRef.current.push(currentIndex);
            playTrackAt(next, { userInitiated: false, gapMs: AUTOPLAY_GAP_MS });
        } else {
            advanceSkippingLocked(currentIndex, 1, AUTOPLAY_GAP_MS);
        }
    };

    // ── Native <audio> event wiring ─────────────────────────────────
    const handleAudioPlay = () => {
        setIsPlaying(true);
        isPlayingRef.current = true;
        const track = currentTrackRef.current;
        if (track && !isTrackGated(track)) {
            trackingService.startPlayTracking(track.id, audioRef.current);
        }
    };
    const handleAudioPause = () => {
        setIsPlaying(false);
        isPlayingRef.current = false;
        const track = currentTrackRef.current;
        if (track && audioRef.current && !audioRef.current.ended) {
            trackingService.stopPlayTracking(track.id);
        }
    };

    useEffect(() => {
        return () => {
            if (currentTrackRef.current) trackingService.stopPlayTracking(currentTrackRef.current.id);
            if (gapResumeTimeoutRef.current) clearTimeout(gapResumeTimeoutRef.current);
            audioCtxRef.current?.close();
        };
    }, []);

    // ── Volume / mute ────────────────────────────────────────────────
    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
    }, [volume]);

    const handleVolumeChange = (v) => {
        setVolume(v);
        if (v > 0 && isMuted) setIsMuted(false);
        if (audioRef.current) audioRef.current.muted = false;
    };

    const toggleMute = () => {
        if (!audioRef.current) return;
        if (isMuted) {
            audioRef.current.muted = false;
            setVolume(prevVolumeRef.current || 0.5);
            setIsMuted(false);
        } else {
            prevVolumeRef.current = volume;
            audioRef.current.muted = true;
            setIsMuted(true);
        }
    };

    // ── Bass boost — keep the live filter node in sync ─────────────
    const handleBassChange = (v) => {
        setBassBoost(v);
        if (bassFilterRef.current) bassFilterRef.current.gain.value = v;
    };

    // ── Seek ─────────────────────────────────────────────────────────
    // Some MP3s report a live `duration` of Infinity (missing VBR/Xing header — a
    // browser quirk, not a data problem); the background probe effect resolves the
    // real length for the whole roll separately, so prefer that when it's available.
    const pctFromPointerEvent = (e) => {
        const bar = seekBarRef.current;
        if (!bar) return 0;
        const rect = bar.getBoundingClientRect();
        return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    };

    const handleSeekPointerDown = (e) => {
        if (!effectiveDuration) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        setIsSeeking(true);
        setCurrentTime(pctFromPointerEvent(e) * effectiveDuration);
    };

    const handleSeekPointerMove = (e) => {
        if (!isSeeking || !effectiveDuration) return;
        setCurrentTime(pctFromPointerEvent(e) * effectiveDuration);
    };

    const handleSeekPointerUp = (e) => {
        if (!isSeeking) return;
        setIsSeeking(false);
        const audio = audioRef.current;
        if (audio && effectiveDuration) {
            const newTime = pctFromPointerEvent(e) * effectiveDuration;
            audio.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    // ── Like / share / link — now-playing cover art actions ─────────
    const handleTrackLike = async () => {
        const track = currentTrackState;
        if (!track) return;
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        try {
            const response = await api.post(`/upload/${track.id}/like`);
            if (response.data.success) {
                const { likeCount, liked } = response.data.data;
                const patch = { like_count: likeCount, user_has_liked: liked };
                setCurrentTrackState(prev => (prev ? { ...prev, ...patch } : prev));
                // Keep the catalog list (and re-selecting this track later) in sync too.
                setDeepWaveTracks(prev => prev.map(t => (t.id === track.id ? { ...t, ...patch } : t)));
                setCommunityTracks(prev => prev.map(t => (t.id === track.id ? { ...t, ...patch } : t)));
            }
        } catch (err) {
            console.error('Error toggling track like:', err);
        }
    };

    const handleTrackShare = () => {
        if (!currentTrackState) return;
        setShareModal({ isOpen: true, track: currentTrackState });
    };

    const handleCopyDirectLink = async () => {
        const track = currentTrackState;
        if (!track) return;
        const url = `${window.location.origin}${trackViewUrl(track)}`;
        try {
            await navigator.clipboard.writeText(url);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            console.error('Error copying track link:', err);
        }
    };

    const currentTrack = currentTrackState;
    const effectiveDuration = (currentTrack && trackDurations[currentTrack.id]) || (isFinite(duration) ? duration : 0);
    const progressPct = effectiveDuration ? (currentTime / effectiveDuration) * 100 : 0;

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader title="The Deep Wave Player" subtitle="An all-access radio deck for The Deep Wave's music" />

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-16">
                {deepWaveLoading ? (
                    <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                        <Loader className="w-8 h-8 animate-spin mb-3" />
                        Loading The Deep Wave catalog...
                    </div>
                ) : deepWaveError ? (
                    <div className="glass-card p-8 text-center text-gray-400">{deepWaveError}</div>
                ) : deepWaveTracks.length === 0 ? (
                    <div className="glass-card p-8 text-center text-gray-400">No tracks available yet.</div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Deck */}
                        <div className="lg:col-span-2 deck-panel p-4 sm:p-6">
                            {/* Visualizer */}
                            <div className="relative h-56 sm:h-64 rounded-lg overflow-hidden bg-black/40 mb-4">
                                <PlayerVisualizer
                                    analyser={analyserReady ? analyserRef.current : null}
                                    isPlaying={isPlaying}
                                    style={visualizerStyle}
                                    colorScheme={colorScheme}
                                    settings={visualizerSettings}
                                />
                                <button
                                    onClick={() => setShowSettings(s => !s)}
                                    aria-label="Visualizer settings"
                                    className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg transition-colors"
                                >
                                    <Settings2 className="w-4 h-4 text-gray-300" />
                                </button>
                            </div>

                            {showSettings && (
                                <div className="glass-card p-3 sm:p-4 mb-4 space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Visualizer Style</p>
                                        <div className="flex flex-wrap gap-2">
                                            {VISUALIZER_STYLES.map(styleName => (
                                                <button
                                                    key={styleName}
                                                    onClick={() => setVisualizerStyle(styleName)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                                                        visualizerStyle === styleName ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                                                    }`}
                                                >
                                                    {styleName}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Color Scheme</p>
                                        <div className="flex flex-wrap gap-2">
                                            {Object.keys(COLOR_SCHEMES).map(scheme => (
                                                <button
                                                    key={scheme}
                                                    onClick={() => setColorScheme(scheme)}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                                                        colorScheme === scheme ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                                                    }`}
                                                >
                                                    <span className="flex gap-0.5">
                                                        {COLOR_SCHEMES[scheme].slice(0, 3).map((c, i) => (
                                                            <span key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />
                                                        ))}
                                                    </span>
                                                    {scheme}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-2 text-xs text-gray-400">
                                        <input
                                            type="checkbox"
                                            checked={visualizerSettings.mirrorMode}
                                            onChange={(e) => setVisualizerSettings(prev => ({ ...prev, mirrorMode: e.target.checked }))}
                                            className="rounded"
                                        />
                                        Mirror mode
                                    </label>
                                </div>
                            )}

                            {/* Now playing */}
                            <div className="text-center mb-4">
                                <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                                    {currentTrack?.title || 'Select a track to begin'}
                                </h2>
                                <p className="text-sm text-gray-400">The Deep Wave</p>
                            </div>

                            {/* Seek bar */}
                            <div className="flex items-center gap-3 mb-5">
                                <span className="text-xs text-gray-400 tabular-nums w-10 text-right">{formatTime(currentTime)}</span>
                                <div
                                    ref={seekBarRef}
                                    onPointerDown={handleSeekPointerDown}
                                    onPointerMove={handleSeekPointerMove}
                                    onPointerUp={handleSeekPointerUp}
                                    onPointerCancel={handleSeekPointerUp}
                                    className="relative flex-1 h-1.5 bg-gray-700 rounded-full cursor-pointer touch-none select-none"
                                >
                                    <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full pointer-events-none" style={{ width: `${progressPct}%` }} />
                                    <div
                                        className={`absolute top-1/2 w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md pointer-events-none transition-transform ${isSeeking ? 'scale-125' : ''}`}
                                        style={{ left: `${progressPct}%` }}
                                    />
                                </div>
                                <span className="text-xs text-gray-400 tabular-nums w-10">{formatTime(effectiveDuration)}</span>
                            </div>

                            {/* Transport + knobs */}
                            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setIsShuffled(s => !s)}
                                        aria-pressed={isShuffled}
                                        title="Shuffle on track end"
                                        className={`p-2.5 rounded-lg transition-colors ${isShuffled ? 'bg-purple-600 text-white' : 'bg-gray-800/60 text-gray-400 hover:text-white'}`}
                                    >
                                        <Shuffle className="w-4 h-4" />
                                    </button>
                                    <button onClick={handlePrev} aria-label="Previous" className="p-2.5 rounded-lg bg-gray-800/60 text-gray-300 hover:text-white transition-colors">
                                        <SkipBack className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={handlePlayPause}
                                        aria-label={isPlaying ? 'Pause' : 'Play'}
                                        className="chrome-ring w-16 h-16 rounded-full flex items-center justify-center"
                                    >
                                        {isPlaying ? (
                                            <Pause className="w-7 h-7 text-gray-900" />
                                        ) : (
                                            <Play className="w-7 h-7 text-gray-900 ml-0.5" />
                                        )}
                                    </button>
                                    <button onClick={handleNext} aria-label="Next" className="p-2.5 rounded-lg bg-gray-800/60 text-gray-300 hover:text-white transition-colors">
                                        <SkipForward className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setIsAutoplay(a => !a)}
                                        aria-pressed={isAutoplay}
                                        title="Autoplay next track"
                                        className={`p-2.5 rounded-lg transition-colors ${isAutoplay ? 'bg-purple-600 text-white' : 'bg-gray-800/60 text-gray-400 hover:text-white'}`}
                                    >
                                        <Repeat className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="flex items-center gap-5">
                                    <RotaryKnob
                                        value={isMuted ? 0 : volume}
                                        onChange={handleVolumeChange}
                                        min={0} max={1} step={0.02} defaultValue={0.8}
                                        label="Volume"
                                        displayValue={(v) => `${Math.round(v * 100)}%`}
                                        accentColor="#d946ef"
                                    />
                                    <RotaryKnob
                                        value={bassBoost}
                                        onChange={handleBassChange}
                                        min={0} max={15} step={0.5} defaultValue={0}
                                        label="Bass Boost"
                                        displayValue={(v) => `${v.toFixed(1)}dB`}
                                        accentColor="#8b5cf6"
                                    />
                                    <button onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'} className="p-2 text-gray-400 hover:text-white transition-colors">
                                        {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                    </button>
                                </div>

                                <button
                                    onClick={handleShuffleNow}
                                    className="px-4 py-2 bg-gray-800/60 hover:bg-gray-700 text-sm text-gray-300 hover:text-white rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <Shuffle className="w-3.5 h-3.5" />
                                    Shuffle Now
                                </button>
                            </div>

                            <audio
                                ref={audioRef}
                                onPlay={handleAudioPlay}
                                onPause={handleAudioPause}
                                onEnded={handleEnded}
                                onTimeUpdate={(e) => { if (!isSeeking) setCurrentTime(e.target.currentTime); }}
                                onLoadedMetadata={(e) => {
                                    const loadedDuration = e.target.duration;
                                    setDuration(loadedDuration);
                                    const trackId = currentTrackRef.current?.id;
                                    if (trackId && isFinite(loadedDuration) && loadedDuration > 0) {
                                        setTrackDurations((prev) => ({ ...prev, [trackId]: loadedDuration }));
                                    }
                                }}
                                preload="metadata"
                            />
                        </div>

                        {/* Track list — placed before the cover art in source order so it always
                            keeps the same grid slot (beside the deck on desktop, right after it on
                            mobile), instead of being pushed down whenever the cover art appears. */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                                The Deep Wave Audio Roll ({tracks.length})
                            </h3>
                            <div className="flex gap-1 mb-3 bg-gray-800/60 p-1 rounded-lg w-fit">
                                <button
                                    onClick={() => setCatalogMode('deepwave')}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                        catalogMode === 'deepwave' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
                                    }`}
                                >
                                    The Deep Wave
                                </button>
                                <button
                                    onClick={() => setCatalogMode('community')}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                        catalogMode === 'community' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
                                    }`}
                                >
                                    Community
                                </button>
                            </div>
                            {loading ? (
                                <div className="glass-card p-6 flex items-center justify-center text-gray-400">
                                    <Loader className="w-5 h-5 animate-spin" />
                                </div>
                            ) : error ? (
                                <div className="glass-card p-6 text-center text-sm text-gray-400">{error}</div>
                            ) : tracks.length === 0 ? (
                                <div className="glass-card p-6 text-center text-sm text-gray-400">No tracks available yet.</div>
                            ) : (
                                <PlayerTrackList
                                    tracks={tracks}
                                    currentIndex={currentIndex}
                                    isPlaying={isPlaying}
                                    isAuthenticated={isAuthenticated}
                                    hasActiveSubscription={hasActiveSubscription}
                                    trackDurations={trackDurations}
                                    onSelectTrack={handleSelectTrack}
                                />
                            )}
                        </div>

                        {/* Now-playing cover art */}
                        {currentTrack?.cover_image_url && (
                            <div className="lg:col-span-2 glass-card p-4 sm:p-6 flex flex-col items-center">
                                <img
                                    key={currentTrack.id}
                                    src={currentTrack.cover_image_url}
                                    alt={currentTrack.title || 'Cover art'}
                                    className="w-full max-w-sm rounded-xl shadow-2xl object-cover"
                                    onContextMenu={(e) => e.preventDefault()}
                                    onDragStart={(e) => e.preventDefault()}
                                />
                                <p className="mt-3 text-sm text-gray-400 text-center truncate max-w-full">
                                    {currentTrack.title}
                                </p>
                                <button
                                    onClick={() => currentTrack.username && navigate(`/profile/${currentTrack.username}`)}
                                    className="text-xs text-purple-300 hover:text-purple-200 transition-colors truncate max-w-full"
                                >
                                    {currentTrack.artist || currentTrack.display_name || 'Unknown Artist'}
                                </button>

                                <div className="flex items-center gap-4 mt-4">
                                    <button
                                        onClick={handleTrackLike}
                                        aria-label={currentTrack.user_has_liked ? 'Unlike' : 'Like'}
                                        className={`flex items-center gap-1.5 text-sm transition-colors ${
                                            currentTrack.user_has_liked ? 'text-red-400 hover:text-red-300' : 'text-gray-400 hover:text-red-400'
                                        }`}
                                    >
                                        <Heart className={`w-4 h-4 ${currentTrack.user_has_liked ? 'fill-current' : ''}`} />
                                        {(currentTrack.like_count || 0).toLocaleString()}
                                    </button>

                                    <button
                                        onClick={handleTrackShare}
                                        aria-label="Share"
                                        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-blue-400 transition-colors"
                                    >
                                        <Share2 className="w-4 h-4" />
                                        Share
                                    </button>

                                    <span className="flex items-center gap-1.5 text-sm text-gray-400">
                                        <Eye className="w-4 h-4" />
                                        {(currentTrack.play_count || 0).toLocaleString()}
                                    </span>

                                    <button
                                        onClick={handleCopyDirectLink}
                                        aria-label="Copy direct link"
                                        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-purple-300 transition-colors"
                                    >
                                        <LinkIcon className="w-4 h-4" />
                                        {linkCopied ? 'Copied!' : 'Link'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, track: null })}
                accessLevel={accessModal.track?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={trackViewUrl(accessModal.track)}
            />

            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, track: null })}
                mediaTitle={shareModal.track?.title}
                mediaType="audio"
                mediaUrl={shareModal.track ? `${window.location.origin}${trackViewUrl(shareModal.track)}` : null}
                username={shareModal.track?.username}
                artistName={shareModal.track?.artist || shareModal.track?.display_name}
                imageUrl={shareModal.track?.cover_image_url}
            />
        </div>
    );
};

export default Player;
